import {useFirebaseState} from "./firebaseContext";
import {useEffect, useState, useCallback} from "react";
import {collection, getDocs, doc, addDoc, updateDoc, deleteDoc, orderBy, query} from "firebase/firestore";
import {DEFAULT_INSTRUCTOR_TRAININGS} from "../calendar/defaultInstructorTrainings";

export const useInstructorTrainings = ({forceRefresh} = {}) => {
    const [trainings, setTrainings] = useState(DEFAULT_INSTRUCTOR_TRAININGS);
    const [loading, setLoading] = useState(true);
    let db = null;
    try {
        const firebaseState = useFirebaseState();
        db = firebaseState?.db || null;
    } catch {
        db = null;
    }

    const fetchTrainings = useCallback(async () => {
        if (!db) {
            setLoading(false);
            return;
        }

        try {
            const trainingsRef = collection(db, "instructorTrainings");
            const q = query(trainingsRef, orderBy("order", "asc"));
            const querySnapshot = await getDocs(q);

            if (querySnapshot && !querySnapshot.empty) {
                const items = [];
                querySnapshot.forEach((docSnap) => {
                    items.push({id: docSnap.id, ...docSnap.data()});
                });
                setTrainings(items);
            } else {
                setTrainings(DEFAULT_INSTRUCTOR_TRAININGS);
            }
        } catch {
            // Fallback to default trainings if collection isn't yet created
            setTrainings(DEFAULT_INSTRUCTOR_TRAININGS);
        } finally {
            setLoading(false);
        }
    }, [db]);

    useEffect(() => {
        fetchTrainings();
    }, [fetchTrainings, forceRefresh]);

    const saveTraining = async (data) => {
        if (!db) return;
        const now = new Date();
        const payload = {
            title: (data.title || "").trim(),
            subtitle: (data.subtitle || "").trim(),
            format: data.format || "digital",
            status: data.status || "open",
            instructorName: (data.instructorName || "").trim(),
            location: (data.location || "").trim(),
            scheduleSummary: (data.scheduleSummary || "").trim(),
            registrationDeadline: data.registrationDeadline || "",
            registrationEmail: (data.registrationEmail || "").trim(),
            infoUrl: (data.infoUrl || "").trim(),
            price: (data.price || "").trim(),
            sessions: Array.isArray(data.sessions) ? data.sessions : [],
            order: Number(data.order) || 1,
            published: data.published !== false,
            updatedAt: now,
        };

        if (data.id && !data.id.startsWith("default-")) {
            const docRef = doc(db, "instructorTrainings", data.id);
            await updateDoc(docRef, payload);
        } else {
            payload.createdAt = now;
            const collectionRef = collection(db, "instructorTrainings");
            await addDoc(collectionRef, payload);
        }
        await fetchTrainings();
    };

    const deleteTraining = async (id) => {
        if (!db || !id || id.startsWith("default-")) return;
        const docRef = doc(db, "instructorTrainings", id);
        await deleteDoc(docRef);
        await fetchTrainings();
    };

    return {
        trainings,
        loading,
        refresh: fetchTrainings,
        saveTraining,
        deleteTraining,
    };
};
